import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, Check, Code, Key, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Integration = () => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    loadApiKey();
  }, []);

  const loadApiKey = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/integration/key`, {
        withCredentials: true
      });
      setApiKey(response.data.api_key);
      setApiUrl(response.data.api_url);
    } catch (error) {
      console.error("Error loading API key:", error);
      toast.error("Failed to load API credentials");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied to clipboard!`);
    setTimeout(() => setCopied(""), 2000);
  };

  const fullCode = `// Configuration
const CONFIG = {
    apiUrl: '${apiUrl}',
    apiKey: '${apiKey}'
};

// Function to save images
async function saveImagesToBackend(imageUrls, userEmail, projectName) {
    const base64Images = await Promise.all(
        imageUrls.map(async (imageUrl) => {
            if (imageUrl.startsWith('data:')) return imageUrl.split(',')[1];
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result.split(',')[1]);
                reader.readAsDataURL(blob);
            });
        })
    );
    
    const response = await fetch(\`\${CONFIG.apiUrl}/webhook/save-images\`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-API-Key': CONFIG.apiKey
        },
        body: JSON.stringify({
            user_email: userEmail,
            project_name: projectName,
            images: base64Images
        })
    });
    
    if (!response.ok) {
        throw new Error(\`Failed to save images: \${response.statusText}\`);
    }
    
    return await response.json();
}

// Function to deduct credits
async function deductCredits(userEmail, creditsUsed, projectId, projectName) {
    const response = await fetch(\`\${CONFIG.apiUrl}/credits/deduct\`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-API-Key': CONFIG.apiKey
        },
        body: JSON.stringify({
            user_email: userEmail,
            credits_used: creditsUsed,
            project_id: projectId,
            project_name: projectName
        })
    });
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to deduct credits');
    }
    
    return await response.json();
}

// Add to your download button
downloadButton.addEventListener('click', async () => {
    const email = prompt('Enter email:') || 'guest@loopai.com';
    const name = prompt('Project name:') || \`Project_\${Date.now()}\`;
    const creditsPerImage = 10; // Adjust based on your pricing
    
    try {
        // Save images
        const result = await saveImagesToBackend(
            images.map(img => img.url),
            email,
            name
        );
        
        // Deduct credits
        const creditsUsed = images.length * creditsPerImage;
        await deductCredits(email, creditsUsed, result.project_id, name);
        
        alert(\`✅ Success! \${result.images_saved} images saved! \${creditsUsed} credits used.\`);
    } catch (error) {
        alert('⚠️ Error: ' + error.message);
        console.error('Error details:', error);
    }
    
    // Download locally
    images.forEach((img, i) => {
        const link = document.createElement('a');
        link.href = img.url;
        link.download = \`\${name}_\${i + 1}.png\`;
        link.click();
    });
});`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-6xl mx-auto py-8">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="mb-4"
          >
            ← Back to Dashboard
          </Button>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            API Integration
          </h1>
          <p className="text-gray-600">
            Connect your Gemini app using API key authentication
          </p>
        </div>

        <div className="grid gap-6">
          {/* API Credentials */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Your API Credentials
              </CardTitle>
              <CardDescription>
                Use these credentials to connect your Gemini app
              </CardDescription>
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium">
                  ✅ CORS Configured: Your app domains are whitelisted!
                </p>
                <p className="text-xs text-green-600 mt-1">
                  • Image Creator: {process.env.REACT_APP_IMAGE_CREATOR_URL || 'https://loopai-image-creatorx-1072188409386.us-west1.run.app'}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  • Video Creator: {process.env.REACT_APP_VIDEO_CREATOR_URL || 'https://loopai-moviex-v1-742433218349.us-west1.run.app'}
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  API URL
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-4 py-3 rounded-lg font-mono text-sm">
                    {apiUrl}
                  </code>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copyToClipboard(apiUrl, "API URL")}
                  >
                    {copied === "API URL" ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  API Key
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-4 py-3 rounded-lg font-mono text-sm">
                    {apiKey}
                  </code>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copyToClipboard(apiKey, "API Key")}
                  >
                    {copied === "API Key" ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Complete Code */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5" />
                Complete Integration Code
              </CardTitle>
              <CardDescription>
                Copy this entire code and paste it in your Gemini app
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-6 rounded-lg overflow-x-auto text-sm">
                  <code>{fullCode}</code>
                </pre>
                <Button
                  size="sm"
                  className="absolute top-4 right-4"
                  onClick={() => copyToClipboard(fullCode, "Code")}
                >
                  {copied === "Code" ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Code
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-purple-50">
            <CardHeader>
              <CardTitle>Integration Steps</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3 text-gray-700">
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600">1.</span>
                  <span>
                    <strong>Open your Gemini app code</strong> in Gemini AI Studio
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600">2.</span>
                  <span>
                    <strong>Copy the code above</strong> and paste it in your JavaScript file
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600">3.</span>
                  <span>
                    <strong>Adjust variable names</strong> (images, img.url, downloadButton) to match your code
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600">4.</span>
                  <span>
                    <strong>Save and test</strong> - Generate images and click download
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600">5.</span>
                  <span>
                    <strong>Check dashboard</strong> - Your images will appear here!
                  </span>
                </li>
              </ol>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="text-lg">Test Backend</CardTitle>
                <CardDescription>
                  Verify your API key is working
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const testCode = `
const CONFIG = {
    apiUrl: '${apiUrl}',
    apiKey: '${apiKey}'
};

fetch(\`\${CONFIG.apiUrl}/webhook/save-images\`, {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
        'X-API-Key': CONFIG.apiKey
    },
    body: JSON.stringify({
        user_email: 'test@example.com',
        project_name: 'API Test',
        images: ['iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8DwHwAFBQIAX8jx0gAAAABJRU5ErkJggg==']
    })
}).then(r => r.json()).then(d => console.log('✅ Test Result:', d));
                    `;
                    copyToClipboard(testCode.trim(), "Test Code");
                    toast.success("Test code copied! Paste in browser console and run.");
                  }}
                >
                  Copy Test Code
                </Button>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="text-lg">Documentation</CardTitle>
                <CardDescription>
                  Complete integration guide
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.open('/app/GEMINI_INTEGRATION_WITH_API_KEY.js', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Full Guide
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Integration;
